import React, { useState, useEffect } from 'react';
import { Content } from '../../Models/Content';
import { useTemplates } from '../../hooks/useTemplates';
import API_ROUTES from '../../hooks/apiRoutes';

import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Autocomplete,
    Chip,
    LinearProgress,
    Typography
} from '@mui/material';
import { FaCloudUploadAlt, FaCheckCircle } from 'react-icons/fa';
import './ContentForm.css';

// O helper de upload vivia AQUI dentro. Saiu para `lib/upload.js` quando o painel novo
// (Pages/Admin/Contents.jsx) passou a precisar do mesmo comportamento — mesma assinatura,
// mesmo retorno; só as mensagens de erro melhoraram. Ver o porquê do XHR lá.
import { uploadToBunny } from '../../lib/upload';

const ContentForm = ({ open, onClose, onSubmit, initialData }) => {
    const { templates, fetchTemplates } = useTemplates();

    const [formData, setFormData] = useState(initialData || {
        title: '',
        subtitle: '',
        description: '',
        contentUrl: '',
        thumbnailUrl: '',
        contentType: 'video',
        userRoles: [],
        templateId: '',
        additionalInfo: '',
        addMore: false
    });

    const [videoUp, setVideoUp] = useState({ loading: false, progress: 0, error: '', done: false });
    const [thumbUp, setThumbUp] = useState({ loading: false, progress: 0, error: '', done: false });

    const handleVideoUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setVideoUp({ loading: true, progress: 0, error: '', done: false });
        try {
            const res = await uploadToBunny(
                API_ROUTES.UPLOADS.video(),
                file,
                { title: formData.title || file.name },
                (p) => setVideoUp((s) => ({ ...s, progress: p }))
            );
            setFormData((prev) => ({
                ...prev,
                contentUrl: res.embedUrl,
                thumbnailUrl: prev.thumbnailUrl || res.thumbnailUrl || ''
            }));
            setVideoUp({ loading: false, progress: 100, error: '', done: true });
        } catch (err) {
            setVideoUp({ loading: false, progress: 0, error: err.message, done: false });
        }
    };

    const handleThumbUpload = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        setThumbUp({ loading: true, progress: 0, error: '', done: false });
        try {
            const res = await uploadToBunny(
                API_ROUTES.UPLOADS.thumbnail(),
                file,
                {},
                (p) => setThumbUp((s) => ({ ...s, progress: p }))
            );
            setFormData((prev) => ({ ...prev, thumbnailUrl: res.url }));
            setThumbUp({ loading: false, progress: 100, error: '', done: true });
        } catch (err) {
            setThumbUp({ loading: false, progress: 0, error: err.message, done: false });
        }
    };

    // Carregar templates quando o formulário abrir
    useEffect(() => {
        if (open) {
            fetchTemplates();
        }
    }, [open, fetchTemplates]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCheckboxChange = (e) => {
        const { name, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        const apiData = new Content({
            id: initialData?.id || initialData?.content_id,
            title: formData.title,
            subtitle: formData.subtitle,
            description: formData.description,
            contentType: formData.contentType,
            thumbnailUrl: formData.thumbnailUrl,
            contentUrl: formData.contentUrl,
            userRoles: formData.userRoles,
            additionalInfo: formData.additionalInfo || '',
            templateId: formData.templateId
        })

        onSubmit(apiData);

        if (!formData.addMore) {
            onClose();
        }
    };

    useEffect(() => {
        if (initialData) {
            setFormData({
                title: initialData.title || initialData.content_title || '',
                subtitle: initialData.subtitle || initialData.content_subtitle || '',
                description: initialData.description || initialData.content_description || '',
                contentType: initialData.contentType || initialData.content_type || 'video',
                thumbnailUrl: initialData.thumbnailUrl || initialData.content_thumbnail_url || '',
                contentUrl: initialData.contentUrl || initialData.content_url || '',
                userRoles: initialData.userRoles || initialData.content_access || [],
                templateId: initialData.templateId || initialData.content_template_id || '',
                additionalInfo: initialData.additionalInfo || initialData.content_additional_info || '',
                addMore: false
            });
        } else {
            // Reset form when no initial data
            setFormData({
                title: '',
                subtitle: '',
                description: '',
                contentUrl: '',
                thumbnailUrl: '',
                contentType: 'video',
                userRoles: [],
                templateId: '',
                additionalInfo: '',
                addMore: false
            });
        }
    }, [initialData]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            className="content-form-dialog"
        >
            <DialogTitle>
                {initialData ? 'Editar conteúdo' : 'Novo conteúdo'}
            </DialogTitle>
            <form onSubmit={handleSubmit}>
                <DialogContent>
                    <Box className="form-section">
                        <TextField
                            name="title"
                            label="Título do conteúdo"
                            value={formData.title}
                            onChange={handleChange}
                            fullWidth
                            required
                            margin="normal"
                        />

                        <TextField
                            name="subtitle"
                            label="Subtítulo"
                            value={formData.subtitle}
                            onChange={handleChange}
                            fullWidth
                            margin="normal"
                        />

                        <TextField
                            name="description"
                            label="Descrição do conteúdo"
                            value={formData.description}
                            onChange={handleChange}
                            fullWidth
                            multiline
                            rows={4}
                            margin="normal"
                        />

                        <Box sx={{ mt: 2, mb: 1, p: 1.5, border: '1px dashed #555', borderRadius: 1 }}>
                            <Button
                                variant="outlined"
                                component="label"
                                fullWidth
                                color={videoUp.done ? 'success' : 'primary'}
                                disabled={videoUp.loading}
                                startIcon={videoUp.done ? <FaCheckCircle /> : <FaCloudUploadAlt />}
                            >
                                {videoUp.loading
                                    ? `Enviando vídeo... ${videoUp.progress}%`
                                    : videoUp.done
                                        ? 'Vídeo enviado — trocar'
                                        : 'Enviar vídeo (Bunny Stream)'}
                                <input type="file" accept="video/*" hidden onChange={handleVideoUpload} />
                            </Button>
                            {videoUp.loading && (
                                <LinearProgress variant="determinate" value={videoUp.progress} sx={{ mt: 1 }} />
                            )}
                            {videoUp.error && (
                                <Typography variant="caption" color="error">{videoUp.error}</Typography>
                            )}
                            {videoUp.done && (
                                <Typography variant="caption" sx={{ color: '#4caf50', display: 'block', mt: 0.5 }}>
                                    Enviado ao Bunny — já pode salvar. O vídeo fica pronto em instantes (transcodificação).
                                </Typography>
                            )}
                        </Box>

                        <TextField
                            name="contentUrl"
                            label="URL do vídeo"
                            value={formData.contentUrl}
                            onChange={handleChange}
                            fullWidth
                            margin="normal"
                            helperText="Preenchida automaticamente pelo upload — ou cole uma URL manualmente. Deixe em branco para conteúdo sem vídeo."
                        />

                        <Box sx={{ mt: 1, mb: 1, p: 1.5, border: '1px dashed #555', borderRadius: 1 }}>
                            <Button
                                variant="outlined"
                                component="label"
                                fullWidth
                                color={thumbUp.done ? 'success' : 'primary'}
                                disabled={thumbUp.loading}
                                startIcon={thumbUp.done ? <FaCheckCircle /> : <FaCloudUploadAlt />}
                            >
                                {thumbUp.loading
                                    ? `Enviando capa... ${thumbUp.progress}%`
                                    : thumbUp.done
                                        ? 'Capa enviada — trocar'
                                        : 'Enviar capa personalizada (imagem)'}
                                <input type="file" accept="image/*" hidden onChange={handleThumbUpload} />
                            </Button>
                            {thumbUp.loading && (
                                <LinearProgress variant="determinate" value={thumbUp.progress} sx={{ mt: 1 }} />
                            )}
                            {thumbUp.error && (
                                <Typography variant="caption" color="error">{thumbUp.error}</Typography>
                            )}
                        </Box>

                        {formData.thumbnailUrl && (
                            <Box sx={{ mb: 1 }}>
                                <img
                                    src={formData.thumbnailUrl}
                                    alt="Prévia da capa"
                                    style={{ maxHeight: 120, borderRadius: 6, border: '1px solid #444' }}
                                />
                            </Box>
                        )}

                        <TextField
                            name="thumbnailUrl"
                            label="URL da capa do conteúdo"
                            value={formData.thumbnailUrl}
                            onChange={handleChange}
                            fullWidth
                            required
                            margin="normal"
                        />

                        <FormControl fullWidth margin="normal">
                            <InputLabel style={{ color: '#bdbdbd' }}>Template</InputLabel>
                            <Select
                                name="templateId"
                                value={formData.templateId || ''}
                                onChange={handleChange}
                                required
                                disabled={templates.length === 0}
                                MenuProps={{
                                    PaperProps: {
                                        style: {
                                            backgroundColor: '#232323',
                                            color: '#fff',
                                        },
                                    },
                                }}
                                sx={{
                                    backgroundColor: '#181818',
                                    color: '#fff',
                                    '.MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#444',
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#d32f2f',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: '#d32f2f',
                                    },
                                    '.MuiSvgIcon-root': {
                                        color: '#fff',
                                    },
                                }}
                            >
                                {templates.map((template) => (
                                    <MenuItem key={template.template_id} value={template.template_id} style={{ backgroundColor: '#232323', color: '#fff' }}>
                                        {template.template_name}
                                    </MenuItem>
                                ))}
                            </Select>
                            {templates.length === 0 && (
                                <div style={{ color: '#999', fontSize: '0.75rem', marginTop: '4px' }}>
                                    Carregando templates...
                                </div>
                            )}
                        </FormControl>

                        <FormControl fullWidth margin="normal">
                            <InputLabel>Tipo do conteúdo</InputLabel>
                            <Select
                                name="contentType"
                                value={formData.contentType}
                                onChange={handleChange}
                                required
                            >
                                <MenuItem value="video">Vídeo</MenuItem>
                                <MenuItem value="document">Documento</MenuItem>
                            </Select>
                        </FormControl>

                        <Autocomplete
                            multiple
                            freeSolo={true}
                            options={['free', 'premium']}
                            value={formData.userRoles}
                            onChange={(event, newValue) => {
                                setFormData({ ...formData, userRoles: newValue });
                            }}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => {
                                    const { key, ...rest } = getTagProps({ index });
                                    return (
                                        <Chip
                                            key={option}
                                            variant="outlined"
                                            label={option}
                                            sx={{
                                                backgroundColor: '#fff',
                                                color: '#222',
                                                borderColor: '#d32f2f',
                                                fontWeight: 600,
                                            }}
                                            {...rest}
                                        />
                                    );
                                })
                            }
                            renderInput={(params) => (
                                <TextField {...params} label="Níveis de acesso" margin="normal" />
                            )}
                        />

                        <TextField
                            name="additionalInfo"
                            label="Informações adicionais (HTML)"
                            value={formData.additionalInfo}
                            onChange={handleChange}
                            fullWidth
                            multiline
                            rows={4}
                            margin="normal"
                        />

                        <Box className="form-footer">
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        name="addMore"
                                        checked={formData.addMore}
                                        onChange={handleCheckboxChange}
                                        color="primary"
                                    />
                                }
                                label="Adicionar mais após salvar"
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} color="inherit">
                        Cancelar
                    </Button>
                    <Button type="submit" variant="contained" color="primary">
                        {initialData ? 'Salvar' : 'Criar'}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default ContentForm; 